import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { User } from '@/types/domain/user';
import { mockUserManagementService } from '@/services/mock/MockUserManagementService';
import {
  X,
  ShieldAlert,
  UserCheck,
  UserX,
  Lock,
  AlertCircle,
} from 'lucide-react';

export type UserActionType = 'ACTIVATE' | 'DEACTIVATE' | 'BLOCK' | 'UNBLOCK' | 'UNLOCK';

interface UserStatusActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  actionType: UserActionType;
  onSuccess: (updatedUser: User) => void;
  currentAdmin?: User | null;
  activeAdminCount?: number;
}

export const UserStatusActionModal: React.FC<UserStatusActionModalProps> = ({
  isOpen,
  onClose,
  user,
  actionType,
  onSuccess,
  currentAdmin,
  activeAdminCount = 2,
}) => {
  if (!isOpen || !user) return null;

  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Self-protection check
  const isSelf = currentAdmin && currentAdmin.id === user.id;
  const isDestructive = actionType === 'DEACTIVATE' || actionType === 'BLOCK';
  const isLastAdmin = user.role === 'ADMIN' && activeAdminCount <= 1;

  const getActionConfig = () => {
    switch (actionType) {
      case 'DEACTIVATE':
        return {
          title: `Deactivate ${user.name}?`,
          icon: UserX,
          color: 'text-amber-600',
          bg: 'bg-amber-500/10',
          buttonLabel: 'Deactivate User Access',
          buttonVariant: 'destructive' as const,
          description:
            'This user will no longer be able to log into ProcureAI. Active approval requests routed to this user will pause until reassigned.',
          requiresReason: true,
        };
      case 'BLOCK':
        return {
          title: `Quarantine & Block ${user.name}?`,
          icon: ShieldAlert,
          color: 'text-rose-600',
          bg: 'bg-rose-500/10',
          buttonLabel: 'Enforce Security Block',
          buttonVariant: 'destructive' as const,
          description:
            'Immediate security containment. All active tokens, sessions, and BAPI integration privileges will be terminated.',
          requiresReason: true,
        };
      case 'ACTIVATE':
        return {
          title: `Activate ${user.name}?`,
          icon: UserCheck,
          color: 'text-emerald-600',
          bg: 'bg-emerald-500/10',
          buttonLabel: 'Activate User',
          buttonVariant: 'default' as const,
          description:
            'Full enterprise authorization will be enabled. The user can authenticate and perform assigned operations.',
          requiresReason: false,
        };
      case 'UNBLOCK':
        return {
          title: `Restore Access for ${user.name}?`,
          icon: UserCheck,
          color: 'text-emerald-600',
          bg: 'bg-emerald-500/10',
          buttonLabel: 'Clear Block & Restore Access',
          buttonVariant: 'default' as const,
          description:
            'Security containment hold will be cleared and the user account will transition back to ACTIVE.',
          requiresReason: false,
        };
      case 'UNLOCK':
        return {
          title: `Clear Lockout for ${user.name}?`,
          icon: Lock,
          color: 'text-purple-600',
          bg: 'bg-purple-500/10',
          buttonLabel: 'Unlock Account',
          buttonVariant: 'default' as const,
          description:
            'Failed login attempt counter will be reset to zero, allowing normal password authentication.',
          requiresReason: false,
        };
    }
  };

  const config = getActionConfig();
  const Icon = config.icon;

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (isSelf && isDestructive) {
      setErrorMessage('Security Violation: You cannot deactivate or block your own administrative account.');
      return;
    }

    if (isLastAdmin && isDestructive) {
      setErrorMessage('Security Violation: You cannot deactivate or block the last active System Administrator.');
      return;
    }

    if (config.requiresReason && !reason.trim()) {
      setErrorMessage('A justification reason is required for compliance audit logs.');
      return;
    }

    try {
      setIsSubmitting(true);
      let updatedUser: User;
      const adminCtx = currentAdmin ? { id: currentAdmin.id, email: currentAdmin.email, role: currentAdmin.role } : undefined;

      switch (actionType) {
        case 'ACTIVATE':
          updatedUser = await mockUserManagementService.activateUser(user.id, reason, adminCtx);
          break;
        case 'DEACTIVATE':
          updatedUser = await mockUserManagementService.deactivateUser(user.id, reason, adminCtx);
          break;
        case 'BLOCK':
          updatedUser = await mockUserManagementService.blockUser(user.id, reason, adminCtx);
          break;
        case 'UNBLOCK':
          updatedUser = await mockUserManagementService.unblockUser(user.id, reason, adminCtx);
          break;
        case 'UNLOCK':
          updatedUser = await mockUserManagementService.unlockUser(user.id, adminCtx);
          break;
      }

      onSuccess(updatedUser);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Action failed.';
      setErrorMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 font-poppins">
      <div className="fixed inset-0 bg-black/65 backdrop-blur-md" onClick={onClose} />

      <div className="relative bg-card border border-border w-full max-w-md rounded-3xl shadow-2xl overflow-hidden z-10 p-6 space-y-4 animate-in zoom-in-95 duration-200 text-xs">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-2xl ${config.bg} ${config.color} shrink-0`}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-foreground tracking-tight">
                {config.title}
              </h3>
              <span className="text-[11px] text-foreground-secondary font-mono">
                {user.employeeId} • {user.email}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Self-Protection Alert */}
        {isSelf && isDestructive && (
          <div className="p-3.5 rounded-2xl bg-destructive/10 border border-destructive/30 text-destructive flex items-start gap-2.5">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <strong className="block">Self-Protection Restriction:</strong>
              You are currently signed in as this administrator. ProcureAI security policies prevent self-deactivation or self-blocking.
            </div>
          </div>
        )}

        {/* Last Admin Alert */}
        {isLastAdmin && isDestructive && (
          <div className="p-3.5 rounded-2xl bg-destructive/10 border border-destructive/30 text-destructive flex items-start gap-2.5">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="leading-relaxed">
              <strong className="block">Last Administrator Safeguard:</strong>
              This is the only remaining active System Administrator. You must assign another active Admin before deactivating or blocking this account.
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/30 text-destructive font-semibold">
            {errorMessage}
          </div>
        )}

        <p className="text-foreground-secondary leading-relaxed">
          {config.description}
        </p>

        {/* Reason Field */}
        {config.requiresReason && (
          <div className="space-y-1.5 pt-1">
            <label className="font-bold text-foreground block">
              Administrative Justification Reason <span className="text-rose-500">*</span>
            </label>
            <textarea
              required
              rows={3}
              placeholder="Provide context for compliance audit trail (e.g. Employee offboarded / Security anomaly flag)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 rounded-xl border border-input bg-background text-foreground text-xs focus:ring-2 focus:ring-primary focus:outline-none custom-scrollbar"
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-border/60">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant={config.buttonVariant}
            onClick={handleConfirm}
            isLoading={isSubmitting}
            disabled={isSubmitting || (isSelf && isDestructive) || (isLastAdmin && isDestructive)}
            className="rounded-xl font-bold font-poppins cursor-pointer shadow-xs"
          >
            {config.buttonLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};
